import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo } from "react";
import {
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import MenuItem from "@material-ui/core/MenuItem";
import RefreshIcon from "@material-ui/icons/Refresh";
import EditIcon from "@material-ui/icons/Edit";
import DialogTextEdit from "./dialogTextEdit";
import SettingsBackupRestoreIcon from "@material-ui/icons/SettingsBackupRestore";
import _ from "lodash";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import { sendCommand } from "../modules/playEngineBridge";
import * as previewQueue from "../modules/playEnginePreviewQueue";
import { useEditableValue } from "../customHooks/useEditableValue";
import {
  ROOT_NODE_ID,
  ancestorIds,
  buildVariableTree,
  filterVariableTree,
  indexTree,
  labelArrayChildren,
  labelFromKey,
  pathToDotted,
} from "../modules/playEngineVariableTree";
import { PANEL_BG, BAR_BG, SECTION_DIVIDER, FIELD_LABEL, PROPS_PANEL_CSS } from "./snapshotEditor/panelPalette";
import {
  BASE_LANGUAGE,
  applyGlobalVariableOverrides,
  isOverrideLanguage,
  switchPlayEngineLanguage,
} from "../modules/playEngineLocalization";
import { usePlayEngineGlobalVariables } from "../modules/playEngineGlobalVariablesStore";
import PlayEngineTabSearch from "./playEngineTabSearch";

const CARD_BG = "#474747";
const SPLIT_STORAGE_KEY = "playEngineCustomVariablesSplit";
// Debounce window for version/DB persist. Local UI always updates immediately.
const PERSIST_DEBOUNCE_MS = 200;

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Dotted paths of the leaves that differ between two variable trees.
 *
 * The engine sets one variable at a time (`playengine:set-global-variable`), so
 * restoring a whole tree means finding the leaves that actually moved. A branch
 * that changed shape (object → value, or a key that only one side has) is
 * reported at the branch itself — that is the level the engine can set it at.
 */
function changedVariableKeys(previous, next, path = []) {
  const isTree = (value) => _.isPlainObject(value) || Array.isArray(value);
  if (!isTree(previous) || !isTree(next) || Array.isArray(previous) !== Array.isArray(next)) {
    return _.isEqual(previous, next) ? [] : [pathToDotted(path)];
  }
  const keys = _.union(Object.keys(previous), Object.keys(next));
  return keys.flatMap((key) => changedVariableKeys(previous[key], next[key], [...path, key]));
}

// ─── Primitive field (string / number / boolean) ──────────────────────────────

/**
 * One property, laid out `96px label | ≤280px control | action`.
 *
 * The same geometry containerPlayEngineVersionSettings uses for every row —
 * duplicated rather than imported so this panel does not reach into that file,
 * but it must stay identical: the two panels sit in the same tab rail and a row
 * of a different width reads as broken next to its neighbour.
 */
function FieldRow({ label, action = null, children }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "96px minmax(0, 280px) auto",
        columnGap: "8px",
        alignItems: "center",
        minHeight: "30px",
        padding: "4px 6px",
      }}
    >
      <div
        title={typeof label === "string" ? label : undefined}
        style={{
          fontSize: "12px",
          color: FIELD_LABEL,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </div>
      <div style={{ minWidth: 0 }}>{children}</div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          alignSelf: "end",
          height: "24px",
          gap: "4px",
          whiteSpace: "nowrap",
        }}
      >
        {action}
      </div>
    </div>
  );
}

function RuntimeBadge() {
  return (
    <Tooltip title="This variable was changed by game logic at runtime. Reload the preview to edit." arrow>
      <Chip
        label="Runtime"
        size="small"
        style={{
          backgroundColor: "#5c4100",
          color: "#ffb300",
          height: "18px",
          fontSize: "10px",
        }}
      />
    </Tooltip>
  );
}

/**
 * The marker on a field that carries a translation: which language it belongs
 * to, and the way back to the English value it replaced.
 */
function OverrideTag({ language, onRevert, disabled }) {
  return (
    <>
      <Chip
        label={String(language).toUpperCase()}
        size="small"
        style={{
          backgroundColor: "#0d3c61",
          color: "#90caf9",
          height: "18px",
          fontSize: "10px",
          fontWeight: 700,
        }}
      />
      <Tooltip title="Revert to the English value" arrow>
        <span>
          <IconButton
            size="small"
            onClick={onRevert}
            disabled={disabled}
            style={{ color: "#90caf9", padding: "2px" }}
          >
            <SettingsBackupRestoreIcon style={{ fontSize: "14px" }} />
          </IconButton>
        </span>
      </Tooltip>
    </>
  );
}

function PrimitiveField({ label, value, path, onChange, disabled, isOverridden, language, onRevert }) {
  const action = (
    <>
      {disabled && <RuntimeBadge />}
      {isOverridden && (
        <OverrideTag language={language} onRevert={onRevert} disabled={disabled} />
      )}
    </>
  );

  if (typeof value === "boolean") {
    return (
      <FieldRow label={label} action={action}>
        <Switch
          size="small"
          checked={value}
          onChange={(e) => onChange(path, e.target.checked)}
          color="primary"
          disabled={disabled}
        />
      </FieldRow>
    );
  }

  if (typeof value === "number") {
    return (
      <FieldRow label={label} action={action}>
        <NumberField value={value} path={path} onChange={onChange} disabled={disabled} />
      </FieldRow>
    );
  }

  // string fallback
  return (
    <FieldRow label={label} action={action}>
      <StringField value={value} path={path} onChange={onChange} disabled={disabled} label={label} />
    </FieldRow>
  );
}

/** Controlled number field — only fires onChange when the value is a complete valid number */
function NumberField({ value, path, onChange, disabled }) {
  const [focused, setFocused] = useState(false);
  // While the field has focus it owns what it shows. The engine echoes each
  // value it is given, so a fast burst produces echoes carrying earlier values;
  // adopting one mid-word is what used to rewrite the field under the cursor.
  const { value: local, set: setLocal, release } = useEditableValue(
    String(value ?? ""),
    focused
  );

  function handleChange(e) {
    const str = e.target.value;
    setLocal(str);
    const trimmed = str.trim();
    if (trimmed === "" || trimmed === "-" || trimmed.endsWith(".")) return;
    const num = Number(trimmed);
    if (!isFinite(num)) return;
    onChange(path, num);
  }

  return (
    <TextField
      fullWidth
      size="small"
      value={local}
      onChange={handleChange}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        // Leaving the field is where a normalised value (0.50 → 0.5) may replace
        // what is shown.
        release();
      }}
      onKeyDown={(e) => e.stopPropagation()}
      disabled={disabled}
      InputProps={{ style: { color: disabled ? "#888" : "#fff", fontSize: "12px" } }}
    />
  );
}

// Roughly what fits the five rows the inline box grows to before it scrolls.
const INLINE_ROW_LIMIT = 200;

/** A value the row cannot show without becoming a scrolling window of text. */
function isTooLongForRow(text) {
  const string = typeof text === "string" ? text : "";
  return string.length > INLINE_ROW_LIMIT || string.includes("\n");
}

/** Controlled string field — preserves cursor position */
function StringField({ value, path, onChange, disabled, label }) {
  const [focused, setFocused] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const inputRef = useRef(null);
  const { value: local, set: setLocal, release } = useEditableValue(
    String(value ?? ""),
    focused
  );

  function handleChange(e) {
    const raw = e.target.value;
    if (raw.includes("\\n")) {
      const cursor = e.target.selectionStart;
      const replaced = raw.replace(/\\n/g, "\n");
      const newCursor = raw.slice(0, cursor).replace(/\\n/g, "\n").length;
      setLocal(replaced);
      onChange(path, replaced);
      requestAnimationFrame(() => {
        if (inputRef.current) inputRef.current.setSelectionRange(newCursor, newCursor);
      });
    } else {
      setLocal(raw);
      onChange(path, raw);
    }
  }

  const editor = editorOpen ? (
    <DialogTextEdit
      open={true}
      label={path}
      storageKey={path}
      value={local}
      setValue={(next) => {
        setLocal(next);
        onChange(path, next);
      }}
      onClose={() => setEditorOpen(false)}
    />
  ) : null;

  // A value too long for the row is shown as the button that opens the editor,
  // not as a wall of text nobody can read five lines at a time. The rule is the
  // LENGTH of the value, not whether it parses as JSON: a long description
  // belongs in the editor just as much as a level table does.
  if (isTooLongForRow(local)) {
    return (
      <>
        <Button
          fullWidth
          size="small"
          color="secondary"
          variant="contained"
          startIcon={<EditIcon />}
          disabled={disabled}
          onClick={() => setEditorOpen(true)}
        >
          {String(label || String(path || "").split(".").pop()).toUpperCase()}
        </Button>
        {editor}
      </>
    );
  }

  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: "4px", minWidth: 0 }}>
      <TextField
        fullWidth
        multiline
        minRows={1}
        maxRows={5}
        size="small"
        value={local}
        inputRef={inputRef}
        onChange={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          release();
        }}
        onKeyDown={(e) => e.stopPropagation()}
        disabled={disabled}
        InputProps={{ style: { color: disabled ? "#888" : "#fff", fontSize: "12px" } }}
      />
      <Tooltip title="Open editor" arrow>
        <span>
          <IconButton
            size="small"
            disabled={disabled}
            onClick={() => setEditorOpen(true)}
            style={{ color: FIELD_LABEL, padding: "2px" }}
          >
            <EditIcon style={{ fontSize: "14px" }} />
          </IconButton>
        </span>
      </Tooltip>
      {editor}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * PlayEngine Custom Variables panel.
 *
 * Loads globalVariables from the live iframe (playengine:get-global-variables)
 * or falls back to playEngineConfig.globalVariables in the DB.
 * Each edit fires playengine:set-global-variable with the full dotted key path
 * AND persists to DB via setSelectedVersionPathValue — both debounced.
 *
 * Handles: primitives, nested objects, arrays (including arrays of objects).
 *
 * ── Languages ─────────────────────────────────────────────────────────────
 * What this panel loads is always the ENGLISH base: the engine's
 * getAll() reverts any active translation before serializing, precisely so a
 * snapshot saved while the preview shows Turkish still carries the base.
 *
 * On any other language the base is shown with that language's overrides laid
 * over it, and edits are written to
 * `playEngineConfig.localization.globalVariableOverrides[language]` — a sparse
 * map keyed by dotted path — instead of onto `globalVariables`. So a field left
 * alone in Turkish keeps following English, and reverting one is deleting a key
 * rather than retyping the original.
 *
 * @param props.selectedVersion              {Object}
 * @param props.selectedBase                 {Object}
 * @param props.selectedGame                 {Object}
 * @param props.setSelectedVersionPathValue  {function}
 * @param props.userPermissions              {Object}
 * @param props.createSnackbar               {function}
 * @param props.isPreviewLoaded              {boolean}
 * @param props.iFrameId                     {string}
 * @param props.localizationLanguage         {string}   language chosen elsewhere in the editor
 * @param props.setLocalizationLanguage      {function} report a switch made here
 */
export default function containerPlayEngineCustomVariables(props) {
  const persistDebounceRef = useRef(null);
  // The write the debounce currently owes — see the persistence block below.
  const pendingPersistRef = useRef(null);

  // ── Language ──────────────────────────────────────────────────────────────
  // Seeded the same way Version Settings seeds its own: the snapshot remembers
  // which language was last on screen.
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    const savedLang = _.get(props, "selectedVersion.playEngineConfig.localization.currentLanguage");
    if (savedLang) return savedLang;
    return Object.keys(_.get(props, "selectedBase.supportedLanguages", {}))[0] || BASE_LANGUAGE;
  });
  const [isSwitchingLanguage, setIsSwitchingLanguage] = useState(false);

  const {
    variables,
    overridesByLanguage,
    runtimeChangedKeys,
    loading: isVariablesLoading,
    loadError,
    reload: loadVariables,
    setVariable: setSharedVariable,
    setOverride: setSharedOverride,
    clearOverrides: clearSharedOverrides,
  } = usePlayEngineGlobalVariables({
    iFrameId: props.iFrameId,
    isPreviewLoaded: props.isPreviewLoaded,
    selectedVersion: props.selectedVersion,
  });

  const overrides = useMemo(
    () => _.get(overridesByLanguage, selectedLanguage, {}) || {},
    [overridesByLanguage, selectedLanguage]
  );
  const overridesRef = useRef(overrides);
  const variablesRef = useRef(variables);

  useEffect(() => {
    overridesRef.current = overrides;
  }, [overrides]);

  useEffect(() => {
    variablesRef.current = variables;
  }, [variables]);

  // ── Outliner / properties split ───────────────────────────────────────────
  // The same two-panel shape Version Settings uses: containers in a tree on
  // top, the selected container's fields below, with a draggable divider.
  const containerRef = useRef(null);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [search, setSearch] = useState("");
  const [expandedItems, setExpandedItems] = useState([]);
  const [treeRatio, setTreeRatio] = useState(() => {
    const raw = Number(window.localStorage.getItem(SPLIT_STORAGE_KEY));
    return isFinite(raw) && raw >= 0.2 && raw <= 0.8 ? raw : 0.5;
  });
  // The tab content is wrapped in an auto-height container, so a percentage
  // height collapses. Measure a definite pixel height for the flex split.
  const [availHeight, setAvailHeight] = useState(0);
  // A language switch made in another tab of the same editor. The engine has
  // already been told; only this panel's view has to catch up.
  useEffect(() => {
    const external = props.localizationLanguage;
    if (!external || external === selectedLanguage) return;
    flushPendingPersist();
    setSelectedLanguage(external);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.localizationLanguage]);

  const overrideMode = isOverrideLanguage(selectedLanguage);

  // Both the raw and designed views resolve localization through this same
  // effective tree. This also makes a compound override written by Design
  // visible to Custom Variables' leaf rows, and vice versa.
  const effectiveVariables = useMemo(
    () => applyGlobalVariableOverrides(variables, overrides),
    [variables, overrides]
  );


  // ── Hidden keys from inspectorConfig ──────────────────────────────────────
  const hiddenKeys = React.useMemo(() => {
    const list = _.get(
      props,
      "selectedVersion.playEngineConfig.inspectorConfig.globalVariables.hidden",
      []
    );
    return new Set(Array.isArray(list) ? list : []);
  }, [_.get(props, "selectedVersion.playEngineConfig.inspectorConfig.globalVariables.hidden")]);

  // ── Outliner tree ─────────────────────────────────────────────────────────

  // Rebuilt whenever the base variables change — which includes editing a field
  // an array item is labelled by, so the outliner keeps up with its own labels.
  // Editing a TRANSLATION does not touch `variables`, so the tree stays put.
  const variableTree = useMemo(
    () => labelArrayChildren(buildVariableTree(variables, hiddenKeys), variables),
    [variables, hiddenKeys]
  );

  const nodesById = useMemo(() => indexTree(variableTree), [variableTree]);
  const filteredTree = useMemo(() => filterVariableTree(variableTree, search), [variableTree, search]);
  const filteredNodesById = useMemo(() => indexTree(filteredTree), [filteredTree]);

  /**
   * Keep a valid node selected.
   *
   * The tree is rebuilt from the data, so a snapshot switch (or an edit that
   * removes a branch) can leave the selection pointing at nothing. Falling back
   * to the first row is better than an empty properties panel with a tree that
   * looks like it has a selection.
   */
  useEffect(() => {
    if (variableTree.length === 0) {
      if (selectedNodeId !== null) setSelectedNodeId(null);
      return;
    }
    if (selectedNodeId && nodesById.has(selectedNodeId)) return;
    const first = variableTree[0];
    setSelectedNodeId(first.id);
    setExpandedItems((prev) => (prev.includes(first.id) ? prev : [...prev, first.id]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variableTree, nodesById]);

  /** Reveal a selection made from outside the tree (or restored after a rebuild). */
  useEffect(() => {
    if (!selectedNodeId) return;
    const needed = ancestorIds(selectedNodeId).filter((id) => nodesById.has(id));
    if (needed.length === 0) return;
    setExpandedItems((prev) => {
      const missing = needed.filter((id) => !prev.includes(id));
      return missing.length === 0 ? prev : [...prev, ...missing];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNodeId, nodesById]);

  const searchSelectedId = search.trim() && !filteredNodesById.get(selectedNodeId)?.fields.length
    ? Array.from(filteredNodesById.values()).find((node) => node.fields.length > 0)?.id
    : selectedNodeId;
  const selectedNode = search.trim()
    ? filteredNodesById.get(searchSelectedId)
    : selectedNodeId ? nodesById.get(selectedNodeId) : null;
  const visibleExpandedItems = search.trim()
    ? Array.from(new Set([...expandedItems, ...filteredNodesById.keys()]))
    : expandedItems;

  // ── Split layout ──────────────────────────────────────────────────────────

  /** Drag the divider; the ratio is clamped and persisted on release. */
  function startDividerDrag(e) {
    e.preventDefault();
    const container = containerRef.current;
    if (!container) return;
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = "none";
    function onMove(ev) {
      const rect = container.getBoundingClientRect();
      if (rect.height <= 0) return;
      setTreeRatio(Math.max(0.2, Math.min(0.8, (ev.clientY - rect.top) / rect.height)));
    }
    function onUp() {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.body.style.userSelect = prevUserSelect;
      setTreeRatio((r) => {
        window.localStorage.setItem(SPLIT_STORAGE_KEY, String(r));
        return r;
      });
    }
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  }

  // Size to the nearest scrollable ancestor's visible height rather than the
  // viewport, so that ancestor never scrolls and drags both panels at once.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    function findScrollParent(node) {
      let p = node.parentElement;
      while (p) {
        const oy = window.getComputedStyle(p).overflowY;
        if (oy === "auto" || oy === "scroll") return p;
        p = p.parentElement;
      }
      return null;
    }
    const sp = findScrollParent(el);
    function measure() {
      const rect = el.getBoundingClientRect();
      if (rect.top === 0 && rect.height === 0) return; // hidden / not laid out yet
      const bottom = sp
        ? sp.getBoundingClientRect().top + sp.clientHeight
        : window.innerHeight - 8;
      const next = Math.max(240, Math.floor(bottom - rect.top));
      setAvailHeight((prev) => (prev === next ? prev : next));
    }
    measure();
    // Layout is often unsettled on first open (tab panel sizing), so re-measure
    // across a couple of frames and whenever the scroll parent resizes.
    const raf1 = requestAnimationFrame(measure);
    const raf2 = requestAnimationFrame(() => requestAnimationFrame(measure));
    window.addEventListener("resize", measure);
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      if (sp) ro.observe(sp);
      if (el.parentElement) ro.observe(el.parentElement);
    }
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.removeEventListener("resize", measure);
      if (ro) ro.disconnect();
    };
  }, []);

  // ── Persistence ───────────────────────────────────────────────────────────
  //
  // The debounce owes exactly one write at a time, and which write it is depends
  // on the language the edit was made in. Holding the write itself (rather than
  // a flag) is what lets a language switch or an unmount settle the debt for
  // whichever language it was incurred in.

  /** Write the English base — the whole tree, as loaded. */
  function persistBaseVariables() {
    const latestVars = variablesRef.current;
    if (!latestVars) return;
    props.setSelectedVersionPathValue?.({
      path: "playEngineConfig.globalVariables",
      value: latestVars,
    });
  }

  /** Write one language's sparse override map. */
  function persistOverrides(language) {
    props.setSelectedVersionPathValue?.({
      path: `playEngineConfig.localization.globalVariableOverrides.${language}`,
      value: overridesRef.current,
    });
  }

  function schedulePersist(write) {
    pendingPersistRef.current = write;
    if (persistDebounceRef.current) clearTimeout(persistDebounceRef.current);
    persistDebounceRef.current = setTimeout(() => {
      persistDebounceRef.current = null;
      const owed = pendingPersistRef.current;
      pendingPersistRef.current = null;
      if (owed) owed();
    }, PERSIST_DEBOUNCE_MS);
  }

  function flushPendingPersist() {
    if (persistDebounceRef.current) {
      clearTimeout(persistDebounceRef.current);
      persistDebounceRef.current = null;
    }
    const owed = pendingPersistRef.current;
    pendingPersistRef.current = null;
    if (owed) owed();
  }

  function cancelPendingWrites() {
    if (persistDebounceRef.current) {
      clearTimeout(persistDebounceRef.current);
      persistDebounceRef.current = null;
    }
    pendingPersistRef.current = null;
    previewQueue.discard(props.iFrameId);
  }

  // ── Handle change ─────────────────────────────────────────────────────────
  const handleChange = useCallback((path, newValue) => {
    if (props.userPermissions?.update === false) {
      props.createSnackbar?.(
        "You do not have permission to update, contact your administrator.",
        "warning",
        false
      );
      return;
    }

    const dotted = pathToDotted(path);
    const language = selectedLanguage;
    const isOverride = isOverrideLanguage(language);

    if (isOverride) {
      // The base tree is left exactly as it is — only this language's map moves.
      const next = setSharedOverride(language, dotted, newValue);
      overridesRef.current = next;
      schedulePersist(() => persistOverrides(language));
    } else {
      variablesRef.current = setSharedVariable(path, newValue);
      schedulePersist(persistBaseVariables);
    }

    const continuousPreview = typeof newValue !== "boolean";

    // Live preview. This was debounced on the same 200 ms as the persist, which
    // is too slow to feel live; the queue coalesces on the next frame instead,
    // so typing costs one message per frame and a toggle still lands at once.
    if (props.iFrameId && (isOverride || variablesRef.current)) {
      const latestValue = isOverride
        ? overridesRef.current[dotted]
        : _.get(variablesRef.current, path);
      previewQueue.push(props.iFrameId, previewQueue.previewKey.variable(language, dotted), {
        type: "playengine:set-global-variable",
        key: dotted,
        value: latestValue,
        // With a language the engine registers an override instead of writing
        // the base, so switching back to English still shows the original.
        ...(isOverride ? { language } : {}),
      });
      if (!continuousPreview) previewQueue.flush(props.iFrameId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.iFrameId,
    props.userPermissions,
    props.setSelectedVersionPathValue,
    selectedLanguage,
    setSharedOverride,
    setSharedVariable,
  ]);

  /**
   * Drop this language's override on one field, putting it back on English.
   *
   * Deleting the key is the whole operation — the field then follows the base
   * again, including later edits to it. Writing the English value back as an
   * override would look identical today and stop tracking tomorrow.
   */
  const handleRevert = useCallback((dotted) => {
    if (props.userPermissions?.update === false) {
      props.createSnackbar?.(
        "You do not have permission to update, contact your administrator.",
        "warning",
        false
      );
      return;
    }
    const language = selectedLanguage;
    if (!isOverrideLanguage(language)) return;
    if (!(dotted in overridesRef.current)) return;

    const { languageMap: next } = clearSharedOverrides(language, dotted);
    overridesRef.current = next;

    // A queued preview would re-apply the translation a moment after it was
    // dropped, so it goes before anything else.
    previewQueue.discard(props.iFrameId);

    // Immediate, not debounced: a revert is a single deliberate click. The flush
    // settles whatever was owed (already reading the map with the key removed)
    // and the write below is then the authoritative one.
    flushPendingPersist();
    persistOverrides(language);

    if (props.iFrameId) {
      sendCommand(props.iFrameId, {
        type: "playengine:clear-global-variable-override",
        key: dotted,
        language,
      }).catch(() => {/* iframe may not be ready */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clearSharedOverrides,
    props.iFrameId,
    props.userPermissions,
    props.setSelectedVersionPathValue,
    selectedLanguage,
  ]);

  /** Switch the whole editor to another language. */
  async function handleLanguageSwitch(newLang) {
    if (newLang === selectedLanguage || isSwitchingLanguage) return;
    setIsSwitchingLanguage(true);
    try {
      // The switch rewrites the WHOLE playEngineConfig, cloned from the version
      // this render was given — so an owed write flushed just before it would be
      // cloned away again and lost. It is settled afterwards instead: an override
      // map is written at its own path, on top of whatever the switch left.
      await switchPlayEngineLanguage({
        iFrameId: props.iFrameId,
        selectedVersion: props.selectedVersion,
        setSelectedVersionPathValue: props.setSelectedVersionPathValue,
        language: newLang,
      });
      flushPendingPersist();
      setSelectedLanguage(newLang);
      props.setLocalizationLanguage?.(newLang);
    } finally {
      setIsSwitchingLanguage(false);
    }
  }

  /**
   * The stored config was rewritten by something other than this panel — undo /
   * redo in the snapshot editor, which bumps `externalConfigRevision`.
   *
   * This panel edits its own copy of the variables, so a restored value reaches
   * it only by being read back out of the snapshot. The engine is told as well:
   * undo replays component config only, and the playable would otherwise keep
   * running the variables that were just undone.
   */
  useEffect(() => {
    if (!props.externalConfigRevision) return;

    const persisted = _.get(props, "selectedVersion.playEngineConfig.globalVariables");
    if (!persisted || typeof persisted !== "object") return;

    // Drop queued writes holding the value the undo just took back. The shared
    // store has already merged the persisted change into both panels.
    cancelPendingWrites();

    const restored = _.cloneDeep(persisted);
    const previous = variablesRef.current;

    // Push only what actually moved — one command per changed leaf is what the
    // engine accepts, and replaying every variable would fight the playable's
    // own runtime state.
    if (props.iFrameId && previous) {
      for (const key of changedVariableKeys(previous, restored)) {
        if (!key) continue; // whole-tree replacement — nothing the engine can set
        sendCommand(props.iFrameId, {
          type: "playengine:set-global-variable",
          key,
          value: _.get(restored, key.split(".")),
        }).catch(() => {/* iframe may not be ready */ });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.externalConfigRevision]);

  // Flush pending persist on unmount so edits are not lost. The owed write
  // already knows which language it belongs to.
  useEffect(() => {
    return () => {
      flushPendingPersist();
      previewQueue.discard(props.iFrameId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.setSelectedVersionPathValue]);

  // ── Guard ─────────────────────────────────────────────────────────────────
  if (_.isNil(props.selectedVersion) || _.isNil(props.selectedBase)) return null;

  const languageEntries = Object.entries(_.get(props, "selectedBase.supportedLanguages", {}));
  const addedLanguages = new Set(
    _.get(props, "selectedVersion.playEngineConfig.localization.availableLanguages") || [BASE_LANGUAGE]
  );

  /** One outliner row per container, recursing into its child containers. */
  function renderTreeItem(node) {
    const isRoot = node.kind === "root";
    return (
      <TreeItem
        key={node.id}
        itemId={node.id}
        label={
          <div style={{ display: "flex", alignItems: "center", gap: "6px", minWidth: 0 }}>
            <Tooltip
              title={isRoot ? "Every custom variable in this snapshot" : node.id}
              arrow
            >
              <span
                style={{
                  flex: "0 1 auto",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {node.label}
              </span>
            </Tooltip>
            {node.count !== null && (
              <span style={{ color: "#7d828a", fontSize: "12px", fontWeight: 500, flexShrink: 0 }}>
                [{node.count}]
              </span>
            )}
            {node.primitiveKeys.length > 0 && node.children.length > 0 && (
              <Tooltip title={`${node.primitiveKeys.length} variable(s) on this group`} arrow>
                <span style={{ color: "#5a5f66", fontSize: "12px", fontWeight: 500, flexShrink: 0 }}>
                  ·{node.primitiveKeys.length}
                </span>
              </Tooltip>
            )}
          </div>
        }
      >
        {/* null, not an empty array: a childless row must not draw an expander. */}
        {node.children.length > 0 ? node.children.map(renderTreeItem) : null}
      </TreeItem>
    );
  }

  /** The fields of the selected container — the only inputs mounted at a time. */
  function renderSelectedFields() {
    if (!selectedNode) {
      return (
        <Typography
          variant="body2"
          style={{ color: "#777", textAlign: "center", padding: "24px 16px", display: "block" }}
        >
          Select a group to edit its variables
        </Typography>
      );
    }
    if (selectedNode.primitiveKeys.length === 0) {
      return (
        <Typography
          variant="body2"
          style={{ color: "#777", textAlign: "center", padding: "24px 16px", display: "block" }}
        >
          {selectedNode.label} groups other variables but declares none of its own —
          open it in the tree above.
        </Typography>
      );
    }
    /**
     * One editable row. Overrides are resolved per field rather than by merging
     * a whole tree, so translating never changes `variables` and never rebuilds
     * the outliner.
     */
    function renderLeaf({ dotted, path }, label) {
      const isOverridden = overrideMode && Object.prototype.hasOwnProperty.call(overrides, dotted);
      const value = _.get(effectiveVariables, path);
      return (
        <PrimitiveField
          key={dotted}
          label={label}
          value={value}
          path={path}
          onChange={handleChange}
          disabled={runtimeChangedKeys.has(dotted)}
          isOverridden={isOverridden}
          language={selectedLanguage}
          onRevert={() => handleRevert(dotted)}
        />
      );
    }

    return (
      <div style={{ padding: "10px 12px" }}>
        <Typography
          style={{
            color: "#e6e9ee",
            display: "block",
            fontSize: "15px",
            fontWeight: 700,
            letterSpacing: "0.2px",
            paddingBottom: "8px",
            marginBottom: "10px",
            borderBottom: `1px solid ${SECTION_DIVIDER}`,
          }}
        >
          {selectedNode.label && selectedNode.id === ROOT_NODE_ID
            ? selectedNode.label
            : selectedNode.id}
        </Typography>
        {selectedNode.fields.map((field) =>
          field.kind === "leaf" ? (
            renderLeaf(field, labelFromKey(String(field.key)))
          ) : (
            // A compound value — `{portrait, landscape}`, `{x, y}` — folded in
            // from the tree. It gets a section rather than a single packed row
            // because each half carries its own translation and runtime state,
            // and two independent action cells do not fit on one line.
            <div key={field.dotted} style={{ marginTop: "18px", marginBottom: "18px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "0 6px 4px",
                }}
              >
                <Typography
                  style={{
                    color: "#cdd1d8",
                    fontSize: "13px",
                    fontWeight: 700,
                    letterSpacing: "0.2px",
                    flexShrink: 0,
                  }}
                >
                  {field.label}
                </Typography>
                <span style={{ flex: 1, height: "1px", backgroundColor: SECTION_DIVIDER }} />
              </div>
              {/* The section needs a visible BODY, not just a heading: its rows
                  sit next to the group's own plain fields, and a header alone
                  has no end — the fields declared after a section read as part
                  of it. The rule down the left is where the section stops. */}
              <div
                style={{
                  borderLeft: `2px solid ${SECTION_DIVIDER}`,
                  paddingLeft: "6px",
                  marginLeft: "6px",
                }}
              >
                {field.leaves.map((leaf) => renderLeaf(leaf, leaf.label))}
              </div>
            </div>
          )
        )}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        display: "flex",
        flexDirection: "column",
        height: availHeight ? `${availHeight}px` : "80vh",
        minHeight: 0,
        overflow: "hidden",
        backgroundColor: PANEL_BG,
      }}
    >
      <PlayEngineTabSearch value={search} onChange={setSearch} />
      {/* Toolbar — language + reload, pinned above the split */}
      <div
        style={{
          flex: "0 0 auto",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "8px 6px 8px 12px",
          backgroundColor: BAR_BG,
          borderBottom: `1px solid ${SECTION_DIVIDER}`,
        }}
      >
        {languageEntries.length > 0 && (
          <>
            <Typography style={{ color: "#a7abb2", flexShrink: 0, fontSize: "13px", fontWeight: 500 }}>
              Language
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={selectedLanguage}
              disabled={isSwitchingLanguage || props.userPermissions?.update === false}
              onChange={(e) => handleLanguageSwitch(e.target.value)}
              InputProps={{ style: { color: "#fff", fontSize: "13px", backgroundColor: "#212226", borderRadius: 4, padding: "2px 4px" } }}
              SelectProps={{ MenuProps: { PaperProps: { style: { backgroundColor: CARD_BG } } } }}
            >
              {languageEntries.map(([key, caption]) => {
                const isAdded = key === BASE_LANGUAGE || addedLanguages.has(key);
                return (
                  <MenuItem key={key} value={key} style={{
                    color: isAdded ? "#90caf9" : "#666",
                    fontWeight: isAdded ? 600 : 400,
                  }}>
                    {caption}{isAdded ? " ●" : ""}
                  </MenuItem>
                );
              })}
            </TextField>
          </>
        )}
        <Tooltip title="Reload variables from preview" arrow>
          <span>
            <IconButton
              size="small"
              onClick={loadVariables}
              disabled={isVariablesLoading}
              style={{ color: isVariablesLoading ? "#555" : "#aaa" }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </div>

      {overrideMode && (
        <div
          style={{
            flex: "0 0 auto",
            padding: "4px 12px",
            backgroundColor: "#1e2a35",
            borderBottom: `1px solid ${SECTION_DIVIDER}`,
            color: "#90caf9",
            fontSize: "11px",
          }}
        >
          Editing {String(selectedLanguage).toUpperCase()} — untouched fields follow English
        </div>
      )}

      {isVariablesLoading && !loadError && (
        <LinearProgress style={{ flex: "0 0 auto" }} />
      )}

      {/* Outliner — containers only */}
      <div style={{ flexGrow: treeRatio, flexShrink: 1, flexBasis: 0, minHeight: 0, overflowY: "auto" }}>
        {loadError ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "8px", padding: "12px" }}>
            <Typography variant="body2" style={{ color: "#e57373" }}>{loadError}</Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={loadVariables}
              style={{ color: "#aaa", borderColor: "#666", textTransform: "none", fontSize: "12px" }}
            >
              Retry
            </Button>
          </div>
        ) : variableTree.length === 0 ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "8px", padding: "12px" }}>
            <Typography variant="body2" style={{ color: "#888" }}>
              No variables defined in this snapshot.
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={loadVariables}
              style={{ color: "#aaa", borderColor: "#666", textTransform: "none", fontSize: "12px" }}
            >
              Reload
            </Button>
          </div>
        ) : filteredTree.length === 0 ? (
          <Typography style={{ padding: 12, color: "#888" }}>No knobs match this search.</Typography>
        ) : (
          <SimpleTreeView
            aria-label="playEngine-custom-variables"
            expandedItems={visibleExpandedItems}
            onExpandedItemsChange={(event, itemIds) => setExpandedItems(itemIds)}
            selectedItems={searchSelectedId ?? null}
            onSelectedItemsChange={(event, itemId) => {
              if (itemId) setSelectedNodeId(itemId);
            }}
            expansionTrigger="iconContainer"
            slots={{
              expandIcon: ArrowRightIcon,
              collapseIcon: ArrowDropDownIcon,
            }}
            sx={{
              overflowX: "hidden",
              flexGrow: 1,
              // Compact, Unity/Blender-style rows — the same chrome the
              // Version Settings outliner uses, so the two read as one editor.
              // Deliberately larger and heavier than the Version Settings
              // outliner: rows here are variable-group names read at a glance
              // while hunting for a field, not a dense component list.
              "& .MuiTreeItem-content": {
                padding: "1px 4px",
                borderRadius: 0,
                minHeight: "28px",
                color: "#dfe2e7",
                fontSize: "14px",
              },
              "& .MuiTreeItem-content:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.055)",
              },
              "& .MuiTreeItem-content.Mui-selected, & .MuiTreeItem-content.Mui-selected.Mui-focused": {
                backgroundColor: "rgba(144, 202, 249, 0.20)",
              },
              "& .MuiTreeItem-content.Mui-selected:hover": {
                backgroundColor: "rgba(144, 202, 249, 0.26)",
              },
              "& .MuiTreeItem-content.Mui-focused": {
                backgroundColor: "transparent",
              },
              "& .MuiTreeItem-iconContainer": {
                width: "18px",
                marginRight: "2px",
                color: "#9aa0a8",
                "& svg": { fontSize: "20px" },
              },
              "& .MuiTreeItem-label": {
                fontSize: "14px",
                fontWeight: 600,
                letterSpacing: "0.1px",
                overflow: "hidden",
              },
              "& .MuiTreeItem-group": {
                marginLeft: "11px",
                borderLeft: "1px solid #3a3a3f",
                paddingLeft: "8px",
              },
            }}
          >
            {filteredTree.map(renderTreeItem)}
          </SimpleTreeView>
        )}
      </div>

      {/* Draggable divider */}
      <div
        onPointerDown={startDividerDrag}
        style={{
          flex: "0 0 auto",
          height: "6px",
          cursor: "row-resize",
          backgroundColor: "#2b2b2b",
          borderTop: "1px solid #1f1f1f",
          borderBottom: "1px solid #1f1f1f",
        }}
      />

      {/* Properties panel — the selected container's variables */}
      <div
        className="proto-props-panel"
        style={{
          flexGrow: 1 - treeRatio,
          flexShrink: 1,
          flexBasis: 0,
          minHeight: 0,
          overflowY: "auto",
          backgroundColor: PANEL_BG,
        }}
      >
        {/* The panel carries its own control skin: the version editor keeps every
            tab mounted, but nothing here should depend on a sibling panel being
            on screen for its inputs to look right. */}
        <style>{PROPS_PANEL_CSS}</style>
        {!loadError && renderSelectedFields()}
      </div>
    </div>
  );
}
